import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/api';

const Estadisticas = () => {
  const [statsCredenciales, setStatsCredenciales] = useState(null);
  const [statsAdmin, setStatsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [credencialesRes, adminRes] = await Promise.all([
        api.get('/estadisticas/credenciales'),
        api.get('/estadisticas/admin'),
      ]);
      setStatsCredenciales(credencialesRes.data);
      setStatsAdmin(adminRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total Credenciales',
      value: statsCredenciales?.total_credenciales || 0,
      gradient: 'from-blue-500 to-blue-600',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      title: 'Nuevos Ingresos',
      value: statsCredenciales?.nuevos_ingresos || 0,
      gradient: 'from-green-500 to-green-600',
      icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
    },
    {
      title: 'Logs Hoy',
      value: statsAdmin?.logs_hoy || 0,
      gradient: 'from-orange-500 to-orange-600',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    },
  ];

  return (
    <div className="animate-fadeInUp">
      <h2 className="text-2xl font-bold text-white mb-6">Estadísticas</h2>

      {/* Tarjetas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className={`bg-gradient-to-br ${card.gradient} rounded-2xl shadow-xl p-6 text-white transform hover:scale-[1.02] transition-all duration-300`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">{card.title}</p>
                <p className="text-3xl font-bold">{card.value}</p>
              </div>
              <svg
                className="w-12 h-12 opacity-40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={card.icon}
                />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos y tablas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Departamentos */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Top Departamentos
          </h3>
          <div className="space-y-3">
            {statsCredenciales?.por_departamento
              ?.filter(
                (item) =>
                  item &&
                  item.departamento &&
                  item.departamento.trim() !== '' &&
                  item.cantidad > 0
              )
              .slice(0, 7)
              .map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-orange-200">{item.departamento}</span>
                    <span className="font-medium text-white">
                      {item.cantidad}
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          (item.cantidad /
                            statsCredenciales?.total_credenciales) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              ))}

            {(!statsCredenciales?.por_departamento ||
              statsCredenciales.por_departamento.filter(
                (item) =>
                  item &&
                  item.departamento &&
                  item.departamento.trim() !== '' &&
                  item.cantidad > 0
              ).length === 0) && (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </div>

        {/* Tipo de empleado */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Distribución por Tipo
          </h3>
          <div className="space-y-3">
            {statsCredenciales?.por_tipo_empleado &&
            statsCredenciales.por_tipo_empleado.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statsCredenciales.por_tipo_empleado}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ tipo, percent }) =>
                      `${tipo}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cantidad"
                    nameKey="tipo"
                  >
                    {statsCredenciales.por_tipo_empleado.map((entry, index) => {
                      const COLORS = [
                        '#3B82F6',
                        '#10B981',
                        '#8B5CF6',
                        '#F59E0B',
                        '#EF4444',
                        '#06B6D4',
                        '#EC4899',
                      ];
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(value) => [`${value} credenciales`, 'Cantidad']}
                  />
                  <Legend
                    wrapperStyle={{ color: '#fff' }}
                    formatter={(value) => (
                      <span style={{ color: '#fff' }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
            {(!statsCredenciales?.por_tipo_empleado ||
              statsCredenciales.por_tipo_empleado.length === 0) && (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Administradores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Administradores
          </h3>
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-3xl font-bold text-white">
                {statsAdmin?.total_administradores || 0}
              </p>
              <p className="text-sm text-blue-200">Total administradores</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-400">
                {statsAdmin?.administradores_activos || 0}
              </p>
              <p className="text-sm text-blue-200">Activos</p>
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-green-400 to-green-500 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${((statsAdmin?.administradores_activos || 0) / (statsAdmin?.total_administradores || 1)) * 100}%`,
              }}
            ></div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex justify-between text-sm">
              <span className="text-blue-200">Logs exitosos hoy:</span>
              <span className="font-medium text-green-400">
                {statsAdmin?.logs_exitosos_hoy || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Últimos accesos */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Últimos Accesos
          </h3>
          <div className="space-y-3">
            {statsAdmin?.ultimos_accesos?.slice(0, 5).map((acceso, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-2 border-b border-white/10 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">
                    {acceso.username}
                  </p>
                  <p className="text-xs text-blue-200/60">
                    {new Date(acceso.fecha_intento).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      acceso.fue_exitoso
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'bg-red-500/20 text-red-300 border border-red-500/30'
                    }`}
                  >
                    {acceso.fue_exitoso ? 'Éxito' : 'Fallido'}
                  </span>
                </div>
              </div>
            ))}
            {(!statsAdmin?.ultimos_accesos ||
              statsAdmin.ultimos_accesos.length === 0) && (
              <p className="text-blue-200/60 text-center py-4">
                No hay registros recientes
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Últimas credenciales agregadas */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          Últimas Credenciales Agregadas
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/20">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Nombre
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Departamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {statsCredenciales?.ultimos_agregados?.map((cred) => (
                <tr
                  key={cred.id}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-white">
                    {cred.nombre_completo}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200">
                    {cred.departamento || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200">
                    {cred.tipo_empleado || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200/60">
                    {cred.created_at
                      ? new Date(cred.created_at).toLocaleDateString()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Estadisticas;
